import React, {
    type ElementType,
    type MouseEventHandler,
    useCallback,
    useEffect,
    useImperativeHandle,
} from "react";

import { isCurrentKey } from "./utils/keyboard";
import {
    Box,
    boxForwardRef,
    type BoxComponent,
} from "../../../../helpers/common/Box/Box";
import {
    NavigationType,
    OrientationType,
    useFocusTrap,
} from "../../../../hooks/useFocusTrap";
import { KEY_CODE } from "../../../../utils/dom/keyCodeMapping";
import { useDropdownStore } from "../../store/store";
import { createDisplayNameForDropdownElement } from "../../utils/getDisplayName";
import { createTestIdForDropdownElement } from "../../utils/getTestId";

import type { IMenu } from "./types";

export const DROPDOWN_MENU_TEST_ID = createTestIdForDropdownElement("menu");

/**
 * Компонент с корневой логикой управления навигацией внутри выпадающего окна. Обладает полем as, что позволяет присвоить переданному компоненту
 * все необходимые свойства и обработчики.
 *
 * ВНИМАНИЕ! Компонент, переданный в as, должен раскрыть ВСЕ переданные пропсы в свой активный элемент, иначе навигация работать
 * НЕ БУДЕТ! См. UniCell.
 */
export const MenuCore = boxForwardRef<
    BoxComponent<
        IMenu & {
            /** Дополнительный обработчик нажатия клавиш. Доступны все ключи, кроме тех, что указаны в isCurrentKey(key) */
            onKeyDownJS?: (e: KeyboardEvent) => void;
            withGlobalFocusTrap?: boolean;
        }
    >
>(
    (
        {
            as,
            onKeyDownJS,
            children,
            onMouseEnter,
            onMouseDown,
            onMouseUp,
            withGlobalFocusTrap,
            "data-tsid": tsid = DROPDOWN_MENU_TEST_ID,
            ...props
        },
        ref,
    ) => {
        const {
            options: {
                openByArrowRight,
                closeByEscape,
                closeByTab,
                onOpen,
                onClose,
            },
            isOpen,
            menuRef,
            menuRefs,
            closeTimerRef,
            preventBlurRef,
            withFocusOnActiveElement,
            isSubMenu,
            focusToMenuButton,
            closeMenu,
        } = useDropdownStore();

        const additionalKeyDownHandler = useCallback(
            (e: KeyboardEvent) => {
                switch (e.key) {
                    case KEY_CODE.ESCAPE:
                        e.stopPropagation();

                        focusToMenuButton();
                        if (closeByEscape) {
                            closeMenu();
                        }
                        break;
                    case KEY_CODE.ARROW_RIGHT:
                        // Отменяем скролл через стрелки
                        e.preventDefault();
                        break;
                    case KEY_CODE.ARROW_LEFT:
                        // Отменяем скролл через стрелки
                        e.preventDefault();
                        if (isSubMenu) {
                            if (openByArrowRight) {
                                e.stopPropagation();
                                focusToMenuButton();
                                closeMenu();
                            }
                        }
                        break;
                    case KEY_CODE.TAB:
                        if (isSubMenu) {
                            e.preventDefault();
                            e.stopPropagation();
                            focusToMenuButton();
                            closeMenu();
                        } else if (closeByTab) {
                            closeMenu();
                        }
                        break;
                    default:
                        if (isCurrentKey(e.key)) {
                            onKeyDownJS?.(e);
                        }
                        break;
                }
            },
            [
                closeByEscape,
                focusToMenuButton,
                isSubMenu,
                openByArrowRight,
                closeByTab,
                closeMenu,
                onKeyDownJS,
            ],
        );

        const mouseEnterHandler: MouseEventHandler = useCallback(
            (e) => {
                if (closeTimerRef.current) {
                    clearTimeout(closeTimerRef.current);
                }
                onMouseEnter?.(e);
            },
            [closeTimerRef, onMouseEnter],
        );

        /**
         * При нажатии ЛКМ - блокируем blur у элементов меню, чтобы окно не закрылось
         */
        const mouseDownHandler: MouseEventHandler = useCallback(
            (e) => {
                preventBlurRef.current = true;
                onMouseDown?.(e);
            },
            [onMouseDown, preventBlurRef],
        );

        /**
         * После отпускания ЛКМ - возвращаем флаг в дефолтное состояние
         */
        const mouseUpHandler: MouseEventHandler = useCallback(
            (e) => {
                preventBlurRef.current = false;
                onMouseUp?.(e);
            },
            [onMouseUp, preventBlurRef],
        );

        useFocusTrap(menuRef, {
            isOpen,
            navigation: NavigationType.ARROWS,
            orientation: OrientationType.VERTICAL,
            onKeyDown: additionalKeyDownHandler,
            withAutoFocus: withFocusOnActiveElement,
            focusToPrevElementAfterUnmount: false,
            isSubHook: isSubMenu && withGlobalFocusTrap,
        });

        /** Устанавливаем ref, полученному от родителя, значение от menuRef.current */
        useImperativeHandle(ref, () => menuRef.current);

        useEffect(() => {
            if (isOpen) {
                onOpen?.();
                return () => onClose?.();
            }
        }, [isOpen, onClose, onOpen]);

        // Регистрируем меню в общем стеке
        useEffect(() => {
            const menu = menuRef.current;
            if (menu && isOpen) {
                const menuStack = menuRefs.current!;
                menuStack.push(menu);
                return () => {
                    // Удаляем меню, если его закрыли
                    const index = menuStack.indexOf(menu);
                    menuStack.splice(index, 1);
                };
            }
        }, [isOpen, menuRef, menuRefs]);

        const { customWidthValue, customVarsStyles, ...restProps } = props;

        const safeProps = {
            ...restProps,
            ...(customWidthValue !== undefined && { customWidthValue }),
            ...(customVarsStyles &&
                Object.keys(customVarsStyles).length > 0 && {
                    customVarsStyles,
                }),
        };

        return (
            <Box
                role="menu"
                {...safeProps}
                as={as as ElementType}
                onMouseDown={mouseDownHandler}
                onMouseUp={mouseUpHandler}
                onMouseEnter={mouseEnterHandler}
                data-tsid={tsid}
                aria-hidden={!isOpen}
                ref={menuRef}
            >
                {children}
            </Box>
        );
    },
);

MenuCore.displayName = createDisplayNameForDropdownElement("MenuCore");
